import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { inject, NgModule, provideAppInitializer, provideZoneChangeDetection } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { OAuthModule, OAuthStorage } from 'angular-oauth2-oidc';
import { firstValueFrom } from 'rxjs';
import { environment } from '../environments/environment';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { AssignmentsService } from './assignments/assignments.service';
import { OidcAuthService } from './auth/oidc-auth.service';
import { OidcCallbackComponent } from './auth/oidc-callback/oidc-callback.component';
import { LocalStorageOAuthStorage } from './auth/local-storage-oauth-storage';
import { LoginService } from './login/login.service';
import { CsteamLandingModule } from './csteam-landing/csteam-landing.module';
import { ICTeamLandingModule } from './icteam-landing/landing.module';
import { PostHeadersInterceptor } from './interceptors/headers.interceptors';
import { LoginModule } from './login/login.module';
import { NavbarModule } from './navbar/navbar.module';
import { OnboardingModule } from './onboarding/onboarding.module';
import { RegistrationModule } from './registration/registration.module';
import { AccessTokenInterceptor } from './shared/access-token.interceptor';
import { GroupsService } from './shared/groups.service';
import { ResponseTokenInterceptor } from './shared/response-token.interceptor';
import { ResponseInterceptor } from './shared/response.interceptor';
import { SharedModule } from './shared/shared.module';
import { PROCTORING_CONFIG } from '@papyrus/proctoring';
import { StudentsService } from './students/students.service';
import { LOCALE_ID } from '@angular/core';
import { registerLocaleData } from '@angular/common';
import localeGb from '@angular/common/locales/en-GB';
import { PapyrusLandingModule } from './papyrus-landing/papyrus-landing.module';
import { QlmLandingModule } from './qlm-landing/qlm-landing.module';
import { SettingsModule } from './settings/settings.module';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatIconModule } from '@angular/material/icon';
import { NotificationToastComponent } from './notifications/notification-toast/notification-toast.component';
import { SwitchOverlayComponent } from './shared/switch-overlay/switch-overlay.component';

registerLocaleData(localeGb);

const LandingModule = environment.org === "CSTeam" ? CsteamLandingModule : environment.org === "ICTeam" ? ICTeamLandingModule : environment.org === "QLM" ? QlmLandingModule : PapyrusLandingModule;
@NgModule({
  declarations: [
    AppComponent,
    NotificationToastComponent,
    SwitchOverlayComponent,
    OidcCallbackComponent,
  ],
  bootstrap: [AppComponent],
  imports: [BrowserModule,
    BrowserAnimationsModule,
    LandingModule,
    RegistrationModule,
    LoginModule,
    AppRoutingModule,
    NavbarModule,
    SharedModule,
    OnboardingModule,
    SettingsModule,
    MatSnackBarModule,
    MatIconModule,
    OnboardingModule,
    OAuthModule.forRoot()
  ],
  providers: [
    // Angular 22 removed zone-based change detection from BrowserModule's providers,
    // so an NgModule app boots without a zone CD scheduler (running in the root zone,
    // effectively zoneless): HTTP responses resolve but the view never re-renders. Opt
    // back into zone change detection explicitly. Remove only when migrating to zoneless.
    provideZoneChangeDetection(),
    // Persist OIDC tokens in localStorage (shared across tabs, survives tab close) --
    // see LocalStorageOAuthStorage. Cross-tab refresh coordination lives in CrossTabTokenSync.
    { provide: OAuthStorage, useClass: LocalStorageOAuthStorage },
    // Configure the QLM OIDC client once at startup, then rehydrate user-info: tokens
    // survive a reload from localStorage, so a reload doesn't pass through
    // /auth/callback (which fetches user-info) -- without this, the route guards' hasInfo
    // check would fail on reload and bounce the user into a silent re-login. Best-effort:
    // a revoked/expired token just falls through to /login.
    provideAppInitializer(async () => {
      const oidc = inject(OidcAuthService);
      oidc.configure();
      const loginService = inject(LoginService);
      // Expired access token but a live refresh token: silently mint a new one via the
      // coordinated refresh grant instead of a full AS redirect (guard recovers on failure).
      // Skipped on the OIDC callback page: the code exchange there is about to mint fresh
      // tokens itself, and a bootstrap refresh with the stale pre-login refresh token
      // would just race it (same rationale as CrossTabTokenSync.onOidcCallbackPage).
      const onCallbackPage =
        window.location.pathname ===
        new URL(environment.oidc.redirectUri, window.location.origin).pathname;
      if (!oidc.isLoggedIn && loginService.hasRefreshToken && !onCallbackPage) {
        try {
          await firstValueFrom(loginService.ensureFreshToken$());
        } catch {
          // Refresh token unavailable/dead; guard routes to /login.
        }
      }
      // Skip on the callback page: tokens in localStorage may be another tab's just-written
      // session or a stale leftover, and OidcCallbackComponent always loads user-info after
      // it finishes the code exchange. Hydrating here raced that exchange and surfaced
      // spurious 401 Invalid token on the magic-link waiting tab.
      if (oidc.isLoggedIn && !loginService.hasInfo && !onCallbackPage) {
        try {
          await loginService.getUserInfo();
        } catch {
          // Guard will route to /login for (silent) re-auth.
        }
      }
    }),
    { provide: HTTP_INTERCEPTORS, useClass: PostHeadersInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: AccessTokenInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ResponseInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ResponseTokenInterceptor, multi: true },
    { provide: LOCALE_ID, useValue: 'en-GB' },
    StudentsService,
    GroupsService,
    AssignmentsService,
    // Disable the library's built-in lockout — we own that logic in
    // StudentSubmissionComponent with proper debouncing and per-assignment thresholds.
    {
      provide: PROCTORING_CONFIG,
      useValue: {
        enforceFullscreen: true,
        detectTabSwitch: true,
        detectPaste: true,
        blockRightClick: true,
        maxWarningsBeforeLockout: 0,
      },
    },
    provideHttpClient(withInterceptorsFromDi())
  ]
})
export class AppModule { }
